/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
export enum TimeRanges {
  /** default means that the time range came from JSON */
  DEFAULT = 'default',
  LAST_30_MINUTES = 'last30Minutes',
  LAST_HOUR = 'lastHour',
  LAST_6_HOURS = 'last6Hours',
  LAST_12_HOURS = 'last12Hours',
  LAST_24_HOURS = 'last24Hours',
  LAST_7_DAYS = 'last7Days',
  LAST_MONTH = 'lastMonth',
  LAST_QUARTER = 'lastQuarter',
  LAST_YEAR = 'lastYear',
  LAST_5_YEARS = 'last5Years',
}

/** PAL DateTimePicker kinds
 * TODO: export these from PAL
 */
export enum PickerKinds {
  PRESET = 'PRESET',
  RELATIVE = 'RELATIVE',
  ABSOLUTE = 'ABSOLUTE',
}

export interface TimeRangeValue {
  /** epoch milliseconds */
  start: number;
  /** epoch milliseconds */
  end: number;
  /** number that describes how many of the interval */
  lastNumber?: number;
  /** 'minutes', etc */
  lastInterval?: string;
  /** 'today', or 'yesterday */
  relativeToWhen?: string;
  /** HH:mm formatted time */
  relativeToTime?: string;
}

export interface PALAbsoluteTimeRangeValue {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

export interface PALRelativeTimeRangeValue {
  /** number that describes how many of the interval */
  lastNumber: number;
  /** 'minutes', etc */
  lastInterval: string;
  /** 'today', or 'yesterday */
  relativeToWhen: string;
  /** HH:mm formatted time */
  relativeToTime: string;
}

export interface TimeRangeOption {
  id: TimeRanges;
  label?: string;
  /** offset is in minutes */
  offset: number;
}

// This is the format that the DateTimePicker returns onDateChange
export interface PALGeneralTimeRange {
  timeRangeKind: PickerKinds;
  timeRangeValue: {
    start: Date;
    end: Date;
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
  };
}

export interface PALPresetTimeRange {
  timeRangeKind: PickerKinds.PRESET;
  timeRangeValue: TimeRangeValueOrOption<TimeRangeOption>;
}

export interface PALAbsoluteTimeRange {
  timeRangeKind: PickerKinds.ABSOLUTE;
  timeRangeValue: TimeRangeValueOrOption<TimeRangeValue>;
}

export interface PALRelativeTimeRange {
  timeRangeKind: PickerKinds.RELATIVE;
  timeRangeValue: TimeRangeValueOrOption<TimeRangeValue>;
}

// This works
export type TimeRangeValueOrOption<T> = T extends { id: TimeRanges }
  ? TimeRangeOption
  : TimeRangeValue;

export type PALTimeRange =
  | PALPresetTimeRange
  | PALAbsoluteTimeRange
  | PALRelativeTimeRange
  | PALGeneralTimeRange;
